<app-loading *ngIf="contentReady"></app-loading>

<section class="bb-page">
  <div class="bb-page-shell">

    <header class="bb-page-header">
      <div class="bb-page-title-block">
        <span class="bb-eyebrow">{{ 'brand' | transloco }}</span>
        <h2 class="bb-page-title">{{ 'brand' | transloco }}</h2>
      </div>
    </header>

    <form [formGroup]="data" (ngSubmit)="onSubmit()">
      <div class="bb-card bb-p-20">
        <div class="bb-form-grid">

          @if(!isUpdatePage) {
          <div class="bb-field bb-field-full">
            <label class="bb-label">{{ 'Brand add mode' | transloco }}</label>
            <div class="bb-brand-mode-switch" role="group">
              <button type="button" class="bb-brand-mode-btn" [class.active]="brandAddMode === 'new'"
                (click)="setBrandAddMode('new')">
                <i class="fa-solid fa-plus"></i>
                <span>{{ 'Create new brand' | transloco }}</span>
              </button>
              <button type="button" class="bb-brand-mode-btn" [class.active]="brandAddMode === 'existing'"
                (click)="setBrandAddMode('existing')">
                <i class="fa-solid fa-tags"></i>
                <span>{{ 'Choose existing brand' | transloco }}</span>
              </button>
            </div>
          </div>
          }

          @if(brandAddMode === 'existing' && !isUpdatePage) {
          <div class="bb-field bb-field-full bb-brand-existing-field">
            <div class="bb-flex bb-items-center bb-justify-between bb-gap-12 bb-wrap bb-mb-12">
              <label class="bb-label">{{ 'select brand' | transloco }}</label>
            </div>
            <input type="hidden" formControlName="existing_brand_id" />

            <button type="button" class="bb-brand-existing-trigger" [class.open]="brandDropdownOpen"
              (click)="toggleBrandDropdown()">
              <span>
                @if(getSelectedBrandsCount() > 0) {
                {{ getSelectedBrandsCount() }} {{ 'Selected Brands' | transloco }}
                } @else {
                {{ 'choose brand' | transloco }}
                }
              </span>
              <i class="fa-solid fa-chevron-down"></i>
            </button>

            @if(brandDropdownOpen) {
            <div class="bb-brand-existing-dropdown">
              <div class="bb-brand-existing-search">
                <i class="fa-solid fa-magnifying-glass"></i>
                <input class="bb-input" type="search"
                  [(ngModel)]="systemBrandsSearch"
                  [ngModelOptions]="{standalone: true}"
                  placeholder="{{ 'Search brands by name or description' | transloco }}" />
              </div>

              <div class="bb-brand-dropdown-actions">
                <button type="button" class="bb-btn bb-btn-secondary bb-brand-select-all"
                  (click)="toggleAllSystemBrands()" [disabled]="loadingSystemBrands || availableSystemBrands.length === 0">
                  <i class="fa-solid" [ngClass]="areAllSystemBrandsSelected() ? 'fa-square-check' : 'fa-list-check'"></i>
                  <span>
                    {{ (areAllSystemBrandsSelected() ? 'Clear selection' : 'Select all brands') | transloco }}
                  </span>
                </button>
              </div>

              @if(loadingSystemBrands) {
              <div class="bb-field-hint">{{ 'Loading brands...' | transloco }}</div>
              }

              @if(!loadingSystemBrands && displayedSystemBrands.length > 0) {
              <div class="bb-brand-existing-list" [dir]="isAr ? 'rtl' : 'ltr'">
                @for (brand of displayedSystemBrands; track brand.id) {
                <label class="bb-brand-existing-row" [class.selected]="isSystemBrandSelected(brand)">
                  <input type="checkbox" class="bb-brand-existing-check"
                    [checked]="isSystemBrandSelected(brand)" (change)="toggleSystemBrand(brand)" />

                  <img class="bb-brand-existing-image" [src]="getBrandImage(brand)"
                    (error)="onBrandImageError($event)" [alt]="getBrandDisplayName(brand)" />

                  <span class="bb-brand-existing-main">
                    <strong>{{ getBrandDisplayName(brand) }}</strong>
                    <small>{{ isAr ? (brand.brandname_en || '-') : (brand.brandname_ar || '-') }}</small>
                  </span>
                </label>
                }
              </div>
              }

              @if(!loadingSystemBrands && availableSystemBrands.length > 0 && displayedSystemBrands.length === 0) {
              <div class="bb-field-hint">{{ 'No brands match your search' | transloco }}</div>
              }

              @if(!loadingSystemBrands && availableSystemBrands.length === 0) {
              <div class="bb-field-hint">{{ 'All system brands already exist in this warehouse, or no brands are available.' | transloco }}</div>
              }
            </div>
            }
          </div>
          }

          @if(brandAddMode === 'existing' && getSelectedBrandsCount() > 0) {
          <div class="bb-field bb-field-full">
            <div class="bb-brand-existing-summary">
              <i class="fa-solid fa-circle-check"></i>
              <span>{{ getSelectedBrandsCount() }} {{ 'Selected Brands' | transloco }}</span>
            </div>
          </div>
          }

          @if(brandAddMode === 'new' || isUpdatePage) {
          <div class="bb-field">
            <label class="bb-label" for="brandname_en">{{ 'brandname_en' | transloco }}</label>
            <input
              class="bb-input"
              type="text"
              id="brandname_en"
              formControlName="brandname_en"
              placeholder="{{ 'brandname_en' | transloco }}"
              [ngClass]="{
                'is-invalid': data.get('brandname_en')?.touched && data.get('brandname_en')?.invalid,
                'is-valid': data.get('brandname_en')?.touched && data.get('brandname_en')?.valid
              }"
            />
          </div>

          <div class="bb-field">
            <label class="bb-label" for="brandname_ar">{{ 'brandname_ar' | transloco }}</label>
            <input
              class="bb-input"
              type="text"
              id="brandname_ar"
              formControlName="brandname_ar"
              placeholder="{{ 'brandname_ar' | transloco }}"
              [ngClass]="{
                'is-invalid': data.get('brandname_ar')?.touched && data.get('brandname_ar')?.invalid,
                'is-valid': data.get('brandname_ar')?.touched && data.get('brandname_ar')?.valid
              }"
            />
          </div>

          <div class="bb-field">
            <label class="bb-label" for="brand_description_en">{{ 'brand_description_en' | transloco }}</label>
            <input
              class="bb-input"
              type="text"
              id="brand_description_en"
              formControlName="brand_description_en"
              placeholder="{{ 'brand_description_en' | transloco }}"
              [ngClass]="{
                'is-invalid': data.get('brand_description_en')?.touched && data.get('brand_description_en')?.invalid,
                'is-valid': data.get('brand_description_en')?.touched && data.get('brand_description_en')?.valid
              }"
            />
          </div>

          <div class="bb-field">
            <label class="bb-label" for="brand_description_ar">{{ 'brand_description_ar' | transloco }}</label>
            <input
              class="bb-input"
              type="text"
              id="brand_description_ar"
              formControlName="brand_description_ar"
              placeholder="{{ 'brand_description_ar' | transloco }}"
              [ngClass]="{
                'is-invalid': data.get('brand_description_ar')?.touched && data.get('brand_description_ar')?.invalid,
                'is-valid': data.get('brand_description_ar')?.touched && data.get('brand_description_ar')?.valid
              }"
            />
          </div>

          <div class="bb-field bb-field-full">
            <div class="bb-shift-option-row">
              <div>
                <strong>{{ 'Local Brand' | transloco }}</strong>
                <small>{{ 'Mark this brand as local' | transloco }}</small>
              </div>

              <label class="bb-switch">
                <input
                  type="checkbox"
                  formControlName="is_local_brand"
                  [ngClass]="{
                    'is-invalid': data.get('is_local_brand')?.touched && data.get('is_local_brand')?.invalid,
                    'is-valid': data.get('is_local_brand')?.touched && data.get('is_local_brand')?.valid
                  }"
                />
                <span></span>
              </label>
            </div>
          </div>
          }

          <div class="bb-field bb-field-full bb-flex bb-justify-end">
            <button type="submit" class="bb-btn bb-btn-primary bb-btn-wide" [disabled]="data.invalid">
              {{ isUpdatePage ? ('Edit' | transloco) : ('Submit' | transloco) }}
            </button>
          </div>

        </div>
      </div>
    </form>

  </div>
</section>
